import type { HttpAdapter } from "../core/adapters/axios.adapter"
import { i18n } from "../internationalize/i18n.service"

export type PushStatus = "unsupported" | "denied" | "subscribed" | "idle"

/**
 * Web Push subscription lifecycle (desktop / Android browsers).
 *
 * Opt-in only: nothing happens until the user explicitly calls enable().
 * The service worker (`/sw.js`) is served same-origin by the Cloudflare Worker
 * on app.thebigwhale.io. iOS requires the PWA to be added to the home screen
 * first (PushManager is otherwise absent) — handled by isSupported().
 */
export class PushService {
  constructor(private pushAdapter: HttpAdapter, private vapidPublicKey: string) {}

  /** True when the browser can do Web Push in the current context. */
  isSupported(): boolean {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  async getStatus(): Promise<PushStatus> {
    if (!this.isSupported()) return "unsupported"
    if (Notification.permission === "denied") return "denied"
    const reg = await navigator.serviceWorker.getRegistration()
    const sub = await reg?.pushManager.getSubscription()
    return sub ? "subscribed" : "idle"
  }

  /** Register SW, ask permission, subscribe, and persist the subscription server-side. */
  async enable(): Promise<PushStatus> {
    if (!this.isSupported()) return "unsupported"

    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" })
    await navigator.serviceWorker.ready

    const permission = await Notification.requestPermission()
    if (permission !== "granted") return permission === "denied" ? "denied" : "idle"

    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey),
      }))

    // Capture the app locale at opt-in time so the backend can localise pushes per device.
    await this.pushAdapter.post("/web", { ...sub.toJSON(), lang: i18n.getLanguage() })
    return "subscribed"
  }

  /** Unsubscribe locally and remove the subscription server-side. */
  async disable(): Promise<PushStatus> {
    const reg = await navigator.serviceWorker.getRegistration()
    const sub = await reg?.pushManager.getSubscription()
    if (sub) {
      await this.pushAdapter.delete("/web", { data: { endpoint: sub.endpoint } }).catch(() => {})
      await sub.unsubscribe().catch(() => {})
    }
    return "idle"
  }

  /**
   * Re-register the CURRENT local subscription with the server (idempotent).
   * Call on every app load: if the browser rotated the push subscription
   * (FCM rotation, browser update, etc.), the fresh endpoint gets stored and
   * the stale one is replaced — so the user keeps receiving without having to
   * re-opt-in. No-op if unsupported, not granted, or no local subscription.
   */
  async resync(): Promise<void> {
    try {
      if (!this.isSupported() || Notification.permission !== "granted") return
      const reg = await navigator.serviceWorker.getRegistration()
      const sub = await reg?.pushManager.getSubscription()
      if (!sub) return
      await this.pushAdapter.post("/web", { ...sub.toJSON(), lang: i18n.getLanguage() })
    } catch {
      /* silent: re-sync is best-effort, never blocks app boot */
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const raw = atob(base64)
    const arr = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
    return arr
  }
}
